import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faFolder,
  faDownload,
  faStop
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import StatusBadge from './StatusBadge';
import './CreativeManagerDetails.css';

const POLLING_INTERVAL = 15000; // 15 seconds (same as PacingDetails)
const POLLING_TIMEOUT = 20 * 60 * 1000; // 20 minutes max polling time

/**
 * CreativeManagerDetails component for viewing creative job details and progress
 */
const CreativeManagerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [jobData, setJobData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(true);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const pollingTimeoutRef = useRef(null);
  const pollingStartTimeRef = useRef(null);

  const fetchJobDetails = useCallback(async () => {
    if (!accountId || !id) return;
    try {
      const response = await api.get(getApiUrl(`/api/v2/creative-manager/${id}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });
      if (response.status === 200) {
        setJobData(response.data);
        setError(null);

        // Check if all campaigns are in terminal states (completed/failed)
        // This ensures polling stops immediately when all campaigns finish
        const campaigns = response.data.campaigns || [];
        const allCampaignsComplete = campaigns.length > 0 && campaigns.every(c =>
          c.status === 'completed' || c.status === 'failed'
        );
        if (allCampaignsComplete) {
          console.log('All campaigns completed or failed - stopping polling immediately');
          setIsPolling(false);
          return;
        }

        // Also stop if overall job status is terminal
        const status = response.data.status;
        if (status === 'completed' || status === 'completed_with_errors' || status === 'failed' || status === 'stopped') {
          setIsPolling(false);
        }
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError(err.response?.data?.error || 'Failed to load job details');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, id, user]);

  useEffect(() => { fetchJobDetails(); }, [fetchJobDetails]);

  // Set polling start time when component mounts or job starts processing
  useEffect(() => {
    if (isPolling && !pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
    }
  }, [isPolling]);

  useEffect(() => {
    const handlePollingTimeout = async () => {
      // Update the job status to 'failed' in database
      try {
        await api.patch(getApiUrl(`/api/v2/creative-manager/${id}/status`), {
          accountId,
          userId: user?.userId || user?.id,
          status: 'failed'
        });
        // Refresh to show updated status
        await fetchJobDetails();
      } catch (updateErr) {
        console.error('Failed to update job status:', updateErr);
      }
    };

    if (isPolling && !isLoading) {
      // Check if polling has timed out
      if (pollingStartTimeRef.current && (Date.now() - pollingStartTimeRef.current) > POLLING_TIMEOUT) {
        setIsPolling(false);
        setPollingTimedOut(true);
        console.log('Polling timed out after 20 minutes - updating status to failed');
        handlePollingTimeout();
        return;
      }
      pollingTimeoutRef.current = setTimeout(() => fetchJobDetails(), POLLING_INTERVAL);
    }
    return () => { if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current); };
  }, [isPolling, isLoading, fetchJobDetails, jobData, accountId, id, user]);

  const getPlatformDisplayName = (platform) => {
    const names = {
      'google-ads-search': 'Google Ads Search',
      'google-ads': 'Google Ads Search',
      'google_ads': 'Google Ads Search',
      'facebook': 'Facebook',
      'reddit': 'Reddit',
      'ttd': 'The Trade Desk',
      'dv360': 'DV360',
      'madhive': 'Madhive'
    };
    return names[platform?.toLowerCase()] || platform || 'Unknown';
  };

  // Handle file download
  const handleFileDownload = async (fileId, filename) => {
    try {
      const response = await api.get(getApiUrl(`/api/v2/creative-manager/${id}/download-file/${fileId}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });
      if (response.data?.publicUrl) {
        // Create temporary link and trigger download
        const link = document.createElement('a');
        link.href = response.data.publicUrl;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  // Loading state
  if (isLoading && !jobData) {
    return (
      <div className="cm-details-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading job details...</p>
      </div>
    );
  }

  // Error state
  if (error && !jobData) {
    return (
      <div className="cm-details-error">
        <p>{error}</p>
        <button onClick={() => navigate('/creative-manager')}>Back to Creative Manager</button>
      </div>
    );
  }

  if (!jobData) return null;

  const progress = jobData.progress || { total: 0, pending: 0, processing: 0, retrying: 0, completed: 0, failed: 0, percentComplete: 0 };
  const campaigns = jobData.campaigns || [];
  const files = jobData.files || [];

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="cm-details-container">
      {/* Header - matching PacingDetails style */}
      <div className="cm-details-header">
        <Link to="/creative-manager" className="back-link">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Creative Manager
        </Link>
        <div className="header-content">
          <div className="header-title-row">
            <h1>{jobData.name}</h1>
            <StatusBadge status={jobData.status} />
          </div>
          <div className="header-meta">
            <span className="meta-item">
              <FontAwesomeIcon icon={faFolder} /> {jobData.folderName || 'No folder'}
            </span>
            <span className="meta-item">Created: {formatDate(jobData.createdAt)}</span>
            <span className="meta-item">{campaigns.length} campaigns</span>
          </div>
        </div>
      </div>

      {/* Progress Section - matching PacingDetails style */}
      <div className="cm-progress-section">
        <div className="progress-header">
          <h2>Progress</h2>
          {isPolling && (
            <span className="polling-indicator">
              <FontAwesomeIcon icon={faSpinner} spin /> Auto-refreshing every 15s
              <button
                className="stop-polling-btn"
                onClick={async () => {
                  // Stop polling first
                  setIsPolling(false);
                  if (pollingTimeoutRef.current) {
                    clearTimeout(pollingTimeoutRef.current);
                    pollingTimeoutRef.current = null;
                  }
                  // Update status to 'stopped' in database
                  try {
                    await api.patch(getApiUrl(`/api/v2/creative-manager/${id}/status`), {
                      accountId,
                      userId: user?.userId || user?.id,
                      status: 'stopped'
                    });
                    await fetchJobDetails(); // Refresh to show updated status
                  } catch (err) {
                    console.error('Failed to update job status:', err);
                  }
                }}
                title="Stop auto-refresh"
              >
                <FontAwesomeIcon icon={faStop} />
                Stop
              </button>
            </span>
          )}
          {pollingTimedOut && jobData?.status === 'processing' && (
            <span className="polling-timeout">
              Timed out - <button onClick={fetchJobDetails} className="refresh-btn">Refresh</button>
            </span>
          )}
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress-fill completed"
              style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
            />
            <div
              className="progress-fill failed"
              style={{ width: `${progress.total > 0 ? (progress.failed / progress.total) * 100 : 0}%` }}
            />
          </div>
          <span className="progress-text">{progress.percentComplete || 0}%</span>
        </div>
        <div className="progress-stats">
          <div className="stat">
            <span className="stat-value">{progress.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat pending">
            <span className="stat-value">{progress.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat processing">
            <span className="stat-value">{progress.processing}</span>
            <span className="stat-label">Processing</span>
          </div>
          {progress.retrying > 0 && (
            <div className="stat retrying">
              <span className="stat-value">{progress.retrying}</span>
              <span className="stat-label">Retrying</span>
            </div>
          )}
          <div className="stat completed">
            <span className="stat-value">{progress.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat failed">
            <span className="stat-value">{progress.failed}</span>
            <span className="stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Files Section - Compact inline list (between progress and campaigns) */}
      {files.length > 0 && (
        <div className="cm-files-section-compact">
          <h3>Uploaded Files ({files.length})</h3>
          <div className="files-inline-list">
            {files.map((file, index) => (
              <div key={file.id || index} className="file-item-compact">
                <span className="file-name-compact">{file.originalFilename}</span>
                <span className={`platform-tag-small ${file.detectedPlatform}`}>
                  {getPlatformDisplayName(file.detectedPlatform)}
                </span>
                <button
                  className="file-download-btn"
                  onClick={() => handleFileDownload(file.id, file.originalFilename)}
                  title="Download file"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaigns Table - matching PacingDetails style */}
      {campaigns.length > 0 && (
        <div className="cm-campaigns-section">
          <h2>Campaigns ({campaigns.length})</h2>
          <div className="campaigns-table-container">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th>Platform Campaign ID</th>
                  <th>Campaign Name</th>
                  <th>Platform</th>
                  <th>Files</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="platform-id">{campaign.platformCampaignId}</td>
                    <td className="campaign-name">
                      {campaign.campaignId ? (
                        <Link to={`/campaigns/${campaign.campaignId}`}>{campaign.campaignName || 'View Campaign'}</Link>
                      ) : (
                        campaign.campaignName || '-'
                      )}
                    </td>
                    <td className="platform-cell">
                      <span className={`platform-tag ${campaign.platform}`}>
                        {getPlatformDisplayName(campaign.platform)}
                      </span>
                    </td>
                    <td className="files-count">{campaign.filesAssigned || 0}</td>
                    <td className="status-cell">
                      <StatusBadge status={campaign.status} />
                    </td>
                    <td className="retry-cell">
                      {campaign.retryCount !== undefined && campaign.retryCount !== null ? (
                        <span className="retry-info">
                          {campaign.retryCount + 1}
                          {campaign.currentOperation && campaign.status === 'retrying_assign' && (
                            <span className="retry-operation"> (retrying)</span>
                          )}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="error-cell" title={campaign.errorMessage || ''}>
                      {campaign.errorMessage ? (
                        <span className="error-text">Error during assign</span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeManagerDetails;

